import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Ban, Loader2, ShieldAlert, Trash2, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import TickerSearchInput from '@/components/TickerSearchInput';
import ModalCloseButton from '@/components/ModalCloseButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminBlocklistTickerButton — Round P108as (Feb 2026).
 *
 * Admin-only modal for permanently removing a bad ticker from the
 * database (e.g. LQIG — an ETF that was liquidated + delisted May 2026
 * but was still hanging around emitting a phantom $94 "dividend" that
 * fooled the streak detector because it was actually the liquidation
 * NAV payout).
 *
 * Flow:
 *   1. Admin types ticker → TickerSearchInput hits /api/stocks/search
 *      → dropdown of ticker · name · yield matches, same UX as the
 *      Compare modal + Community journal search.
 *   2. On pick → fetch /api/stocks/{ticker} → render the exact same
 *      badge set the Stock Modal header shows so the admin can
 *      verify they're nuking the right row (ticker, full name,
 *      sector, dividend flag, quality-score band, dividend-tier
 *      badge, current yield).
 *   3. Optional "reason" textarea. Default reason = "manual_admin_delist".
 *   4. Two-stage confirm → POST /api/admin/blocklist/{ticker}.
 *      Backend persists to `dynamic_blocklist`, unions into
 *      in-memory `TICKER_BLOCKLIST`, purges every cache collection,
 *      returns per-collection delete counts.
 *
 * Rendered only for `user.role === 'admin'`.
 */
export default function AdminBlocklistTickerButton() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null); // { ticker, name, ...preview }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmStage, setConfirmStage] = useState(false);

  if (user?.role !== 'admin') return null;

  const reset = () => {
    setSelected(null);
    setReason('');
    setConfirmStage(false);
  };

  const handleSelect = async (ticker) => {
    if (!ticker) return;
    const upper = String(ticker).toUpperCase();
    setPreviewLoading(true);
    setConfirmStage(false);
    try {
      const { data } = await axios.get(`${API}/stocks/${upper}`, { withCredentials: true });
      setSelected({
        ticker: data.ticker || upper,
        name: data.name || data.company_name || upper,
        sector: data.sector || null,
        pays_dividend: data.pays_dividend,
        dividend_yield: data.dividend_yield,
        dividend_rate: data.dividend_rate,
        price: data.price,
        quality_score: data.quality_score,
        dividend_tier: data.dividend_tier,
        exchange: data.exchange,
      });
    } catch (err) {
      toast.error(`Could not load ${upper}`, {
        description: err?.response?.data?.detail || err?.message,
      });
      setSelected({ ticker: upper, name: upper });
    } finally {
      setPreviewLoading(false);
    }
  };

  const blocklist = async () => {
    if (!selected?.ticker || busy) return;
    if (!confirmStage) { setConfirmStage(true); return; }
    setBusy(true);
    try {
      const body = { reason: (reason || '').trim() || 'manual_admin_delist' };
      const { data } = await axios.post(
        `${API}/admin/blocklist/${selected.ticker}`,
        body,
        { withCredentials: true },
      );
      const removed = data?.removed || {};
      const removedSummary = Object.entries(removed)
        .filter(([, n]) => Number(n) > 0)
        .map(([k, n]) => `${k}: ${n}`)
        .join(', ') || 'no cached rows';
      toast.success(`${selected.ticker} blocklisted permanently`, {
        description: `Purged (${removedSummary}). Blocklist size now ${data?.blocklist_size}.`,
      });
      reset();
    } catch (err) {
      toast.error(`Failed to blocklist ${selected.ticker}`, {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const qBand = (() => {
    const q = Number(selected?.quality_score);
    if (!Number.isFinite(q) || q <= 0) return null;
    if (q >= 85) return { label: 'Elite', cls: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/40' };
    if (q >= 70) return { label: 'Strong', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' };
    if (q >= 55) return { label: 'Healthy', cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' };
    if (q >= 40) return { label: 'Average', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' };
    if (q >= 25) return { label: 'Weak', cls: 'bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30' };
    return { label: 'Risky', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' };
  })();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white transition-colors"
        data-testid="admin-blocklist-ticker-btn"
        title="Permanently remove a ticker from the database (admin)"
      >
        <Ban className="w-3.5 h-3.5" />
        Blocklist Ticker
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3"
          onClick={() => { setOpen(false); reset(); }}
          data-testid="admin-blocklist-ticker-modal"
        >
          <div
            className="bg-card border rounded-xl shadow-2xl w-full max-w-lg relative max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
              <h3 className="text-base font-bold flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-rose-500" />
                Blocklist Ticker (permanent)
              </h3>
              <ModalCloseButton onClick={() => { setOpen(false); reset(); }} testId="admin-blocklist-close" />
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Removes the ticker from every cache collection AND adds it
                to <code className="font-mono">dynamic_blocklist</code> so it
                can never be re-cached. Use for delisted, liquidated, or
                phantom-data tickers (e.g. an ETF that paid a liquidation
                NAV that our streak detector misreads as a dividend).
              </p>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                  Ticker
                </label>
                <TickerSearchInput
                  onSelect={handleSelect}
                  placeholder="Search ticker or company name…"
                  testid="admin-blocklist-search"
                  maxResults={10}
                  dividendFilter="all"
                />
              </div>

              {previewLoading && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading ticker preview…
                </div>
              )}

              {selected && !previewLoading && (
                <div
                  className="border rounded-lg p-3 bg-muted/30 space-y-2"
                  data-testid="admin-blocklist-preview"
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-lg font-bold font-mono">{selected.ticker}</span>
                    {selected.exchange && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {selected.exchange}
                      </span>
                    )}
                    <span className="text-sm text-foreground/80 truncate">{selected.name}</span>
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${
                        selected.pays_dividend === false
                          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40'
                          : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40'
                      }`}
                      data-testid="admin-blocklist-divflag"
                    >
                      {selected.pays_dividend === false ? 'Non-Dividend' : 'Dividend'}
                    </span>
                    {qBand && (
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border ${qBand.cls}`}
                        title={`Quality Score ${selected.quality_score}`}
                        data-testid="admin-blocklist-quality-band"
                      >
                        {selected.quality_score} · {qBand.label}
                      </span>
                    )}
                    {selected.dividend_tier && (
                      <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider border bg-violet-500/15 text-violet-700 dark:text-violet-300 border-violet-500/40"
                        data-testid="admin-blocklist-tier"
                      >
                        {selected.dividend_tier}
                      </span>
                    )}
                    {selected.sector && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground border border-border">
                        {selected.sector}
                      </span>
                    )}
                  </div>

                  <div className="text-xs grid grid-cols-3 gap-2 pt-1 text-muted-foreground">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider">Price</div>
                      <div className="font-mono text-foreground">
                        {Number.isFinite(Number(selected.price)) ? `$${Number(selected.price).toFixed(2)}` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider">Yield</div>
                      <div className="font-mono text-foreground">
                        {Number.isFinite(Number(selected.dividend_yield)) ? `${Number(selected.dividend_yield).toFixed(2)}%` : '—'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider">Div rate</div>
                      <div className="font-mono text-foreground">
                        {Number.isFinite(Number(selected.dividend_rate)) ? `$${Number(selected.dividend_rate).toFixed(2)}` : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selected && !previewLoading && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">
                    Reason (optional — logged for audit)
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 200))}
                    placeholder="e.g. ETF liquidated May 2026 — phantom $94 NAV payout misread by streak detector"
                    rows={2}
                    className="w-full px-2 py-1.5 rounded-md text-xs bg-background border border-border focus:outline-none focus:ring-2 focus:ring-rose-500/40 resize-none"
                    data-testid="admin-blocklist-reason"
                    maxLength={200}
                  />
                  <div className="text-[10px] text-muted-foreground mt-0.5 text-right">
                    {200 - (reason || '').length} characters left
                  </div>
                </div>
              )}

              {selected && !previewLoading && confirmStage && (
                <div
                  className="border border-rose-500/50 bg-rose-500/10 rounded-lg p-2.5 text-xs flex items-start gap-2"
                  data-testid="admin-blocklist-confirm"
                >
                  <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    This is <strong>permanent</strong>. {selected.ticker} will
                    be removed from all cache collections and can never be
                    re-cached. Click &quot;Confirm blocklist&quot; once more to commit.
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between gap-2 px-4 py-3 border-t bg-muted/30">
              <button
                type="button"
                onClick={() => { setOpen(false); reset(); }}
                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-background border border-border hover:bg-muted transition-colors"
                data-testid="admin-blocklist-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={blocklist}
                disabled={!selected || previewLoading || busy}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  confirmStage
                    ? 'bg-rose-700 hover:bg-rose-800 text-white ring-2 ring-rose-500/60'
                    : 'bg-rose-600 hover:bg-rose-700 text-white'
                }`}
                data-testid="admin-blocklist-submit"
              >
                {busy
                  ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Blocklisting…</>
                  : <><Trash2 className="w-3.5 h-3.5" /> {confirmStage ? 'Confirm blocklist' : 'Blocklist ticker'}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
